import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { DataScroller } from 'primereact/datascroller';
import { Tag } from 'primereact/tag';
import { Tooltip } from 'primereact/tooltip';
import { Link } from 'react-router-dom';
import ImageComponent from 'shared/ImageComponent';
import { convertDateFromApiToUserTimezone } from 'utils/utility_functions/timezone';
import { UserDetailsContext } from 'context/userDetailsContext';
import '../mailbox.scss';

/**
 * Covers both V2 RDS values (snake_case) and V1 DynamoDB values (UPPER_CASE)
 * so the component works regardless of which format the API returns.
 */
const STATUS_LABEL_MAP = {
  // V2 values
  mapped_by_ai: 'Mapped By AI',
  unmapped_by_ai: 'Unmapped By AI',
  ai_mapping_failed: 'AI Mapping Failed',
  mapped_by_user: 'Mapped By User',
  failed_mail_deliveries: 'Failed Mail Deliveries',
  lawft_origin: 'Originated in Lawft',
  // V1 fallback values
  SUCCESS: 'Mapped By AI',
  NO_MATCH_FOUND: 'Unmapped By AI',
  FAILED: 'AI Mapping Failed',
  MANUAL_OVERRIDE: 'Mapped By User',
  FAILED_MAIL: 'Failed Mail Deliveries',
  LAWFT_ORIGIN: 'Originated in Lawft',
  BYPASSED: 'Mapping Bypassed',
  IN_PROGRESS: 'Mapping In Progress',
  QUEUED: 'Mapping Scheduled',
};

const getStatusClass = (status) => {
  if (status === 'mapped_by_ai' || status === 'SUCCESS') return 's-tag';
  if (['ai_mapping_failed', 'failed_mail_deliveries', 'FAILED', 'FAILED_MAIL'].includes(status)) return 'da-tag';
  if (['unmapped_by_ai', 'NO_MATCH_FOUND'].includes(status)) return 'y-tag';
  if (['mapped_by_user', 'MANUAL_OVERRIDE'].includes(status)) return 'b-tag';
  if (status === 'IN_PROGRESS') return 'b-tag';
  return 'y-tag';
};

/**
 * Resolves the sender display name from a thread object.
 * Prefers V1-style email_from.user_name, then falls back to from_email.
 */
const getSenderDisplayName = (thread) => thread?.email_from?.user_name || thread?.email_from?.user_email || thread?.from_email || '';

/**
 * Resolves the email address used for the avatar initial.
 * Prefers V1-style email_from.user_email, falls back to from_email.
 */
const getSenderEmail = (thread) => thread?.email_from?.user_email || thread?.from_email || '';

const getSenderInitial = (email = '') => email?.charAt(0)?.toUpperCase() || '?';

const MailListV2 = ({
  threads,
  selectedThreadPk,
  setSelectedThreadPkSk,
  handleLoadMore,
  loading,
  hasMore,
  paginationLoading,
  readEmails,
  setReadEmails,
  multiple,
  selectedEmails,
  setSelectedEmails,
}) => {
  const userContext = useContext(UserDetailsContext);
  const [scrollHeight, setScrollHeight] = useState('600px');

  useEffect(() => {
    const calculateScrollHeight = () => {
      const reserved = 320;
      const calculated = window.innerHeight - reserved;
      setScrollHeight(`${Math.max(400, calculated)}px`);
    };
    calculateScrollHeight();
    window.addEventListener('resize', calculateScrollHeight);
    return () => window.removeEventListener('resize', calculateScrollHeight);
  }, []);

  const toggleSelection = (pk, sk) => {
    setSelectedEmails((prev) => {
      const exists = prev.some((e) => e.pk === pk && e.sk === sk);
      if (exists) return prev.filter((e) => !(e.pk === pk && e.sk === sk));

      const next = [...prev, { pk, sk }];
      // Keep currently open thread in selection
      if (selectedThreadPk) {
        const current = threads?.find((t) => t.pk === selectedThreadPk);
        if (current && !next.some((e) => e.pk === current.pk && e.sk === current.sk)) {
          next.push({ pk: current.pk, sk: current.sk });
        }
      }
      return next;
    });
  };

  const handleThreadClick = (thread) => {
    setSelectedThreadPkSk({ pk: thread.pk, sk: thread.sk });
    setReadEmails((prev) => ({ ...prev, [thread.pk]: true }));
    setSelectedEmails((prev) => {
      const filtered = prev.filter((e) => !selectedThreadPk || e.pk !== selectedThreadPk);
      const exists = filtered.some((e) => e.pk === thread.pk && e.sk === thread.sk);
      return exists ? filtered : [...filtered, { pk: thread.pk, sk: thread.sk }];
    });
  };

  const itemTemplate = (thread) => {
    if (!thread) return null;

    const isUnread = thread.is_unread && !readEmails[thread.pk];
    const isChecked = selectedEmails.some((e) => e.pk === thread.pk && e.sk === thread.sk);
    const isSelected = selectedThreadPk === thread.pk;

    const senderDisplayName = getSenderDisplayName(thread);
    const senderEmail = getSenderEmail(thread);
    // Use mail_date (V2) or fall back to time (V1)
    const mailDate = thread.mail_date || thread.time;
    // Use mapping_status (V2) or fall back to ai_reconciliation_status (V1)
    const mappingStatus =
      thread.mapping_status ||
      (thread.failed_delivery ? 'FAILED_MAIL' : thread.lawft_origin ? 'LAWFT_ORIGIN' : thread.ai_reconciliation_status);

    return (
      <div
        className="mail-list-outer-box inner-border-bottom d-flex align-items-stretch"
        style={{ backgroundColor: isSelected ? 'var(--selected-msg)' : 'var(--white)' }}
      >
        <button
          className="flex-grow-1 p-2 pointer mail-list-box border-0 text-start"
          style={{ backgroundColor: isSelected ? 'var(--selected-msg)' : 'var(--white)' }}
          tabIndex="0"
          onClick={() => handleThreadClick(thread)}
        >
          <div className="d-flex flex-column gap-1 w-100">
            {/* Row 1: Avatar + Sender + Status Tag */}
            <div className="d-flex align-items-center gap-2 justify-content-between">
              <div className="d-flex align-items-center gap-2 flex-grow-1 min-w-0">
                {multiple && (
                  <div className="d-flex align-items-center flex-shrink-0">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={(e) => {
                        e.stopPropagation();
                        toggleSelection(thread.pk, thread.sk);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Select email: ${thread.subject}`}
                    />
                  </div>
                )}
                <div className="position-relative flex-shrink-0">
                  <ImageComponent
                    fileName={getSenderInitial(senderEmail)}
                    style={{ backgroundColor: '#2196F3', color: '#ffffff' }}
                    fileSize="medium.jpg"
                  />
                  {isUnread && <i className="icon-window-filled mail-list-badge" aria-hidden="true" />}
                </div>
                <h6 className={`text-truncate black-700 mb-0 ${isUnread ? 'unread-email' : ''}`}>{senderDisplayName}</h6>
              </div>

              {/* Mapping status tag */}
              {mappingStatus && (
                <div className="flex-shrink-0">
                  <Tag
                    className={`tag-case mail-ai-tags text-center d-status-tags d-flex ${getStatusClass(mappingStatus)}`}
                    rounded
                    value={
                      <>
                        <i className="bi bi-stars me-2" aria-hidden="true" />
                        {STATUS_LABEL_MAP[mappingStatus] ?? mappingStatus}
                      </>
                    }
                  />
                </div>
              )}
            </div>

            {/* Row 2: Subject */}
            <div className={`${isUnread ? 'unread-email' : ''} ms-5 ps-3`}>
              <span className={`pointer text-truncate d-block black-700 ${isUnread ? 'text-bold' : ''}`}>{thread.subject}</span>
            </div>

            {/* Row 3: Case/Lead link */}
            {thread.case_id && (
              <div className={`${isUnread ? 'unread-email' : ''} ms-5 ps-3`}>
                <span className="text-truncate black-700 mb-2 mt-2">
                  {'Case/Lead: '}
                  <Link
                    to={`${thread.is_lead ? '/leads' : '/cases'}/${thread.case_id}/mails`}
                    className={`redirection-link-no-text-transform ellipsis-text case-name-tooltip-${thread.index}`}
                  >
                    {thread.case_name}
                  </Link>
                </span>
                <Tooltip target={`.case-name-tooltip-${thread.index}`} className="case-tooltip" content={thread.case_name} position="top" />
              </div>
            )}

            {/* Row 4: Date + Time — only rendered when a date is available */}
            {mailDate && (
              <div className="d-flex gap-2 ms-5 ps-3 mt-1 justify-content-between">
                <span className="text-hint black-600 m-0">
                  {convertDateFromApiToUserTimezone(mailDate, userContext?.userDetails?.timezone, 'MM-DD-YYYY')}
                </span>
                <span className="text-hint black-600 m-0">
                  {convertDateFromApiToUserTimezone(mailDate, userContext?.userDetails?.timezone, 'hh:mm A')}
                </span>
              </div>
            )}
          </div>
        </button>
      </div>
    );
  };

  return (
    <div className="mailbox">
      <div className="msg-holder pt-0 pb-0 position-relative flex-grow-1" style={{ overflow: 'hidden' }}>
        {(loading || paginationLoading) && (
          <div className="chat-loader-container pe-3">
            <div className="overlay">
              <i className="pi pi-spin pi-spinner" style={{ fontSize: '2em' }} aria-label="Loading" />
            </div>
          </div>
        )}
        <DataScroller
          value={threads || []}
          emptyMessage="No emails found"
          itemTemplate={itemTemplate}
          rows={DEFAULT_ROWS}
          inline
          lazy={hasMore}
          scrollHeight={scrollHeight}
          onLazyLoad={handleLoadMore}
        />
      </div>
    </div>
  );
};

const DEFAULT_ROWS = 20;

MailListV2.propTypes = {
  threads: PropTypes.arrayOf(
    PropTypes.shape({
      pk: PropTypes.string,
      sk: PropTypes.string,
      from_email: PropTypes.string,
      to_email: PropTypes.arrayOf(PropTypes.string),
      subject: PropTypes.string,
      mail_date: PropTypes.string,
      is_unread: PropTypes.bool,
      mapping_status: PropTypes.string,
      case_id: PropTypes.string,
      case_name: PropTypes.string,
      is_lead: PropTypes.bool,
    })
  ),
  selectedThreadPk: PropTypes.string,
  setSelectedThreadPkSk: PropTypes.func.isRequired,
  handleLoadMore: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  hasMore: PropTypes.bool,
  paginationLoading: PropTypes.bool,
  readEmails: PropTypes.object,
  setReadEmails: PropTypes.func,
  multiple: PropTypes.bool,
  selectedEmails: PropTypes.array,
  setSelectedEmails: PropTypes.func,
};

MailListV2.defaultProps = {
  threads: [],
  selectedThreadPk: null,
  loading: false,
  hasMore: false,
  paginationLoading: false,
  readEmails: {},
  setReadEmails: () => {},
  multiple: false,
  selectedEmails: [],
  setSelectedEmails: () => {},
};

export default MailListV2;
